import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Typography,
  Popover,
  FormControl,
  IconButton,
  Tooltip,
  Autocomplete,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { connect, useDispatch } from 'react-redux'
import {
  CustomInputLabel,
  CustomTextField,
} from '../../components/restyled-mui/CustomInputs'
import timezones from '../../../src/utils/timezones'
import LockResetIcon from '@mui/icons-material/LockReset'
import PasswordSettingsDialog from '../settings-users/PasswordSettingsDialog'
import clsx from 'clsx'
import Api from '../../api'
import { loggedUser } from '../../redux/actions/user'
import { setSelectedUserSetup } from '../../redux/actions/userSetups'
import { changeStatus } from '../../redux/actions/status'
import { STATUS } from '../../redux/types'

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(5.5, 12, 7.5, 8),
    backgroundColor: theme.palette.menu.bg,
    borderRadius: theme.spacing(2.5),
    boxShadow: 'none',
    border: `1px solid ${theme.palette.menu.border}`,
    width: 280, // fixed width
    minWidth: 280,
    maxWidth: 280,
  },
  innerContent: {
    padding: theme.spacing(3, 0, 5.5, 0),
  },
  title: {
    fontSize: 14,
    color: theme.palette.menu.text,
    height: 'auto',
  },
  bodyTextName: {
    color: theme.palette.menu.text,
    fontSize: 16,
    height: 'auto',
  },
  logoutButton: {
    width: 116,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  settingsButton: {
    marginLeft: 8,
    padding: 4,
  },
  menuIcon: {
    color: theme.palette.menu.icons,
    transition: 'color 0.3s ease',
  },
  selectedMenuIcon: {
    color: theme.palette.primary.main,
  },
  lockContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))

const LogoutMenu = props => {
  const classes = useStyles()
  const { anchorEl, handleClose, logout, user } = props

  const dispatch = useDispatch()

  const units = ['kW', 'kWh', 'MW', 'MWh']

  const [userPreferences, setUserPreferences] = useState({
    username: '',
    usernameError: '',
    email: '',
    emailError: '',
    timezone: '',
    timezoneError: '',
    unit: '',
    unitError: '',
  })

  const [originalPreferences, setOriginalPreferences] = useState({
    unit: '',
    timezone: '',
  })

  const [passwordOpen, setPasswordOpen] = useState(false)
  const [loadingPreferences, setLoadingPreferences] = useState(true)
  const [saving, setSaving] = useState(false)

  const debounceTimerRef = useRef(null)
  const latestPrefsRef = useRef(userPreferences)
  const originalPrefsRef = useRef(originalPreferences)

  useEffect(() => {
    latestPrefsRef.current = userPreferences
  }, [userPreferences])

  useEffect(() => {
    originalPrefsRef.current = originalPreferences
  }, [originalPreferences])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  const handleDialogOpen = () => {
    setPasswordOpen(true)
  }

  const handleDialogClose = () => {
    setPasswordOpen(false)
  }

  const autoSave = useCallback(async () => {
    const prefs = latestPrefsRef.current
    const original = originalPrefsRef.current

    if (!prefs.unit || !prefs.timezone) return

    setSaving(true)
    try {
      const response = await Api.Users.updateUserPreferences({
        unit: prefs.unit,
        timezone: prefs.timezone,
      })

      if (response.status === 200) {
        setOriginalPreferences({ unit: prefs.unit, timezone: prefs.timezone })
        dispatch(
          loggedUser({ ...user, timezone: prefs.timezone, unit: prefs.unit }),
        )
        dispatch(setSelectedUserSetup(null))
        dispatch(changeStatus(STATUS.SUCCESS, response.data.msg))
      } else {
        setUserPreferences(prev => ({
          ...prev,
          unit: original.unit,
          timezone: original.timezone,
        }))
        dispatch(
          changeStatus(
            STATUS.ERROR,
            response.data?.msg || 'Failed to save preferences',
          ),
        )
      }
    } catch {
      setUserPreferences(prev => ({
        ...prev,
        unit: original.unit,
        timezone: original.timezone,
      }))
      dispatch(changeStatus(STATUS.ERROR, 'Failed to save preferences'))
    } finally {
      setSaving(false)
    }
  }, [dispatch, user])

  const handleAutocompleteChange = key => (_, value) => {
    const hasError = !value || value === ''
    setUserPreferences(prev => ({
      ...prev,
      [key]: value,
      [`${key}Error`]: hasError ? 'This field is required' : '',
    }))

    if (!hasError) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(autoSave, 500)
    }
  }

  useEffect(() => {
    const fetchUserPreferences = async () => {
      try {
        const response = await Api.Users.getUserPreferences()

        const { username, unit, timezone, email } = response.data
        const newState = {
          username,
          email,
          unit,
          timezone,
          usernameError: '',
          emailError: '',
          timezoneError: '',
          unitError: '',
        }

        setUserPreferences(newState)
        setOriginalPreferences({ unit, timezone })
      } catch (error) {
        console.error('Error while fetching user preferences API call:', error)
      } finally {
        setLoadingPreferences(false)
      }
    }

    fetchUserPreferences()
  }, [])

  useEffect(() => {
    // When user prop changes, update preferences
    if (user) {
      setUserPreferences(prev => ({
        ...prev,
        unit: user.unit,
        timezone: user.timezone,
      }))
      setOriginalPreferences({
        unit: user.unit,
        timezone: user.timezone,
      })
    }
  }, [user])

  const renderUserContent = () => {
    return (
      !loadingPreferences && (
        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          classes={{
            root: classes.root,
            paper: classes.paper,
          }}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Typography variant="h4" className={classes.title}>
            Profile
          </Typography>

          <div className={classes.lockContainer}>
            <Typography variant="body1" className={classes.bodyTextName}>
              {userPreferences.username}
            </Typography>
            <Tooltip title="Change Password" arrow>
              <IconButton onClick={handleDialogOpen}>
                <LockResetIcon
                  className={clsx(classes.menuIcon, {
                    [classes.selectedMenuIcon]: passwordOpen,
                  })}
                />
              </IconButton>
            </Tooltip>
          </div>
          <PasswordSettingsDialog
            user={user}
            open={passwordOpen}
            onClose={handleDialogClose}
          />

          <>
            <CustomInputLabel>Unit</CustomInputLabel>
            <FormControl fullWidth variant="outlined" size="small">
              <Autocomplete
                variant="outlined"
                size="small"
                options={units}
                value={userPreferences.unit}
                autoHighlight
                onChange={handleAutocompleteChange('unit')}
                renderInput={params => (
                  <CustomTextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      style: { padding: '1px 5px' },
                    }}
                    error={Boolean(userPreferences.unitError)}
                    helperText={userPreferences.unitError}
                  />
                )}
              />
            </FormControl>
          </>
          <>
            <CustomInputLabel>Timezone</CustomInputLabel>
            <FormControl fullWidth variant="outlined" size="small">
              <Autocomplete
                variant="outlined"
                size="small"
                options={timezones}
                value={userPreferences.timezone}
                autoHighlight
                onChange={handleAutocompleteChange('timezone')}
                renderInput={params => (
                  <CustomTextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      style: { padding: '1px 5px' },
                    }}
                    error={Boolean(userPreferences.timezoneError)}
                    helperText={userPreferences.timezoneError}
                  />
                )}
              />
            </FormControl>
          </>

          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.logoutButton}
            onClick={logout}
            disabled={saving}
          >
            Logout
          </Button>
        </Popover>
      )
    )
  }

  // return isAdmin ? renderAdminContent() : renderUserContent()
  return renderUserContent()
}

const mapStateToProps = state => ({
  user: state.user,
})

const mapDispatchToProps = {}

export default connect(mapStateToProps, mapDispatchToProps)(LogoutMenu)
